import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { isFlowTheme } from 'src/Libs/utils'

const MONTH_BUBBLE_SIZE = 36
const BUBBLE_PADDING_RIGHT = 9
const MAX_ITEM_PER_ROW = 6
const MAX_CONTAINER_WIDTH = MONTH_BUBBLE_SIZE * MAX_ITEM_PER_ROW + BUBBLE_PADDING_RIGHT * MAX_ITEM_PER_ROW

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'end',
    maxWidth: MAX_CONTAINER_WIDTH,
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'left',
    },
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    minHeight: 36,
    maxHeight: 36,
    minWidth: 36,
    maxWidth: 36,
    borderRadius: '50%',
    marginLeft: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    borderColor: 'transparent',
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(0),
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  active: {
    backgroundColor: theme.palette.primary.main,
    color: '#ffffff',
    transition: 'all 0.3s ease-in-out',
    '@media (hover: hover)': {
      '&:hover': {
        backgroundColor: theme.palette.grey.grey06,
        borderColor: theme.palette.grey.grey06,
        color: theme.palette.text.header,
      },
    },
    '&.MuiButton-root.Mui-disabled': {
      opacity: 0.7,
    },
  },
  inactive: {
    backgroundColor: theme.palette.grey.grey06,
    color: theme.palette.text.header,
    transition: 'all 0.3s ease-in-out',
    '@media (hover: hover)': {
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        color: '#ffffff',
      },
    },
    '&.MuiButton-root.Mui-disabled': {
      opacity: 0.7,
    },
  },
}))

interface Props {
  selectedMonthList: number[]
  onChangeSelectedMonthList: (selectedMonthList: number[]) => void
  disabled?: boolean
}

/**
 * Selector which displays all months as bubbles and allows to add/remove months (as numbers) to a list.
 *
 * @selectedMonthList the list of currently added months
 * @onChangeSelectedMonthList function to add or remove months to the current list
 */
const MonthSelector: React.FC<Props> = ({ selectedMonthList, onChangeSelectedMonthList, disabled = false }) => {
  const { classes, cx } = useStyles()
  const { t } = useCustomTranslation()

  const onPress = (month: number): void => {
    const isSelected = selectedMonthList.includes(month)

    if (!isSelected) {
      const updatedSelectedMonthList = [...selectedMonthList]
      updatedSelectedMonthList.push(month)
      onChangeSelectedMonthList(updatedSelectedMonthList)
    }
    if (isSelected) {
      const updatedSelectedMonthList = selectedMonthList.filter(selectedMonth => selectedMonth !== month)
      onChangeSelectedMonthList(updatedSelectedMonthList)
    }
  }

  return (
    <Box className={classes.container}>
      {months.map(month => {
        return (
          <Button
            key={`month-${month.translationKey}`}
            className={cx(classes.button, selectedMonthList.includes(month.index) ? classes.active : classes.inactive)}
            onClick={() => onPress(month.index)}
            disabled={disabled}
          >
            <Typography sx={selectedMonthList.includes(month.index) && isFlowTheme ? { color: '#ffffff' } : undefined} variant="body2">
              {t(month.translationKey)}
            </Typography>
          </Button>
        )
      })}
    </Box>
  )
}

export default MonthSelector

const months = [
  {
    index: 0,
    translationKey: 'common.months-short.january',
  },
  {
    index: 1,
    translationKey: 'common.months-short.february',
  },
  {
    index: 2,
    translationKey: 'common.months-short.march',
  },
  {
    index: 3,
    translationKey: 'common.months-short.april',
  },
  {
    index: 4,
    translationKey: 'common.months-short.may',
  },
  {
    index: 5,
    translationKey: 'common.months-short.june',
  },
  {
    index: 6,
    translationKey: 'common.months-short.july',
  },
  {
    index: 7,
    translationKey: 'common.months-short.august',
  },
  {
    index: 8,
    translationKey: 'common.months-short.september',
  },
  {
    index: 9,
    translationKey: 'common.months-short.october',
  },
  {
    index: 10,
    translationKey: 'common.months-short.november',
  },
  {
    index: 11,
    translationKey: 'common.months-short.december',
  },
]
